import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X, Truck, CreditCard } from "lucide-react";
import { useShop } from "@/shop/ShopContext";
import CartDrawer from "@/components/CartDrawer";

export const Logo = () => (
  <Link to="/" className="group flex items-center gap-2.5" data-testid="logo-link">
    <svg width="38" height="38" viewBox="0 0 64 64" className="transition-transform duration-500 group-hover:rotate-[30deg]">
      <path d="M32 2 59 18.5v27L32 62 5 45.5v-27z" fill="#FF5500" />
      <text x="32" y="42" fontFamily="Oswald, Arial, sans-serif" fontSize="23" fontWeight="700" fill="#0C0E12" textAnchor="middle">
        DR
      </text>
    </svg>
    <span className="leading-none">
      <span className="block font-display text-lg font-semibold uppercase tracking-wide text-neutral-100">Delivery</span>
      <span className="block font-display text-[11px] uppercase tracking-[0.4em] text-[#FF5500]">Ricambi</span>
    </span>
  </Link>
);

const Header = () => {
  const { config } = useShop();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navItems = [
    { label: "Catalogo", to: "/catalogo" },
    { label: "Pagamenti", section: "pagamenti" },
    { label: "Contatti", section: "contatti" },
  ];

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-[#FF5500] text-[#0C0E12]">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-1.5 text-xs font-semibold">
          <a href={config.phone_href} className="flex items-center gap-1.5" data-testid="topbar-phone-link">
            <Phone className="h-3.5 w-3.5" />
            <span className="font-display tracking-wider">{config.phone}</span>
          </a>
          <span className="hidden items-center gap-1.5 sm:flex">
            <Truck className="h-3.5 w-3.5" /> Spedizione espressa 24/48h in tutta Italia
          </span>
          <span className="hidden items-center gap-1.5 md:flex">
            <CreditCard className="h-3.5 w-3.5" /> {config.bonifico_enabled ? "Carta · Bonifico" : "Pagamento con carta"}
          </span>
        </div>
      </div>

      <div className="border-b border-white/10 bg-[#0C0E12]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <Logo />
          <nav className="hidden items-center gap-8 md:flex">
            {navItems.map((item) =>
              item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="font-display text-sm uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:text-[#FF5500]"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => goSection(item.section)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="font-display text-sm uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:text-[#FF5500]"
                >
                  {item.label}
                </button>
              )
            )}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={config.phone_href}
              data-testid="header-phone-link"
              className="hidden items-center gap-2 rounded-full border border-[#FF5500]/50 px-4 py-2 font-display text-sm tracking-wider text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white lg:flex"
            >
              <Phone className="h-4 w-4" />
              {config.phone}
            </a>
            <CartDrawer />
            <button
              className="rounded-md p-2 text-neutral-200 md:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="border-t border-white/10 px-4 py-4 md:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link
                to="/catalogo"
                onClick={() => setOpen(false)}
                className="font-display text-base uppercase tracking-[0.2em] text-neutral-200"
                data-testid="mobile-nav-catalogo"
              >
                Catalogo
              </Link>
              <button onClick={() => goSection("pagamenti")} className="text-left font-display text-base uppercase tracking-[0.2em] text-neutral-200" data-testid="mobile-nav-pagamenti">
                Pagamenti
              </button>
              <button onClick={() => goSection("contatti")} className="text-left font-display text-base uppercase tracking-[0.2em] text-neutral-200" data-testid="mobile-nav-contatti">
                Contatti
              </button>
              <a href={config.phone_href} className="flex items-center gap-2 font-display text-base uppercase tracking-wider text-[#FF5500]" data-testid="mobile-phone-link">
                <Phone className="h-4 w-4" /> {config.phone}
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
